<template>
  <v-card>
    <v-card-title>
      <div>
        <div class="headline">Campaign Information</div>
      </div>
      <v-spacer></v-spacer>
      <div slot="action" v-if="!newCampaign">
        <v-tooltip top>
          <v-btn slot="activator" icon @click="doQuery">
            <v-icon color="primary">mdi-magnify</v-icon>
          </v-btn>
          <span>Query Campaign</span>
        </v-tooltip>
      </div>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text>
      <v-flex xs12 class="mt-3">
        <message :messages="messages"></message>
      </v-flex>
      <v-container fluid grid-list-lg>
        <v-layout row wrap>
          <v-flex xs6 v-if="newCampaign">
            <v-text-field :label="labels.campaign" v-model="campaignForm.campaign" :error-messages="errors.collect('campaign')" v-validate="'required|param|max:12'" data-vv-name="campaign" :data-vv-as="labels.campaign"></v-text-field>
          </v-flex>
          <v-flex xs6 v-else>
            <v-subheader class="queryLabel">{{labels.campaignQuery}}</v-subheader>
            <div class="title">{{campaignForm.campaign}}</div>
          </v-flex>
          <v-flex xs6>
            <v-text-field :label="labels.description" v-model="campaignForm.description" :error-messages="errors.collect('description')" v-validate="'required|entry|max:128'" data-vv-name="description" :data-vv-as="labels.description"></v-text-field>
          </v-flex>
          <v-layout row wrap>
            <v-flex xs7>
              <cal-picker v-model="effDate" :label="labels.effDate" v-validate="'required'" :data-vv-as="labels.effDate" data-vv-name="effDate" :error-messages="errors.collect('effDate')" @input="doEffDate"></cal-picker>
            </v-flex>
            <v-flex xs5>
              <time-picker v-model="effTime" :label="labels.effTime" v-validate="'required'" :data-vv-as="labels.effTime" data-vv-name="effTime" :error-messages="errors.collect('effTime')" @input="doEffDate"></time-picker>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs7>
              <cal-picker v-model="expDate" :label="labels.expDate" v-validate="expDateValidation" :min="this.effDate" :data-vv-as="labels.expDate" data-vv-name="expDate" :error-messages="errors.collect('expDate')" @input="doExpDate"></cal-picker>
            </v-flex>
            <v-flex xs5>
              <time-picker v-model="expTime" :label="labels.expTime" v-validate="'required'" :data-vv-as="labels.expTime" data-vv-name="expTime" :error-messages="errors.collect('expTime')" @input="doExpDate"></time-picker>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs6>
              <v-checkbox :label="labels.allIntermediary" v-model="campaignForm.allIntermediary" data-vv-name="allIntermediary" :data-vv-as="labels.allIntermediary"></v-checkbox>
            </v-flex>
            <v-flex xs6>
              <v-checkbox :label="labels.enabled" v-model="campaignForm.enabled" data-vv-name="enabled" :data-vv-as="labels.enabled"></v-checkbox>
            </v-flex>
          </v-layout>
          <v-flex xs12>
            <v-btn color="primary" type="submit" :loading="submitting" @click.prevent="doSave">
              {{newCampaign? 'Add Campaign' : 'Update Campaign'}}
              <v-icon right>{{newCampaign? 'mdi-calendar-plus' : 'mdi-content-save'}}</v-icon>
              </v-btn>
          </v-flex>
        </v-layout>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import MessageMixin from '@/mixins/message'
import toDate from 'date-fns/toDate'
import { formatDisplayTime } from '@/utils/date'
import { expandURI } from '@/utils/ajax'
import { extractMenu } from '@/utils/extract'
import { CAMPAIGN_SIDEMENU } from '@/store/types'

export default {
  data() {
    return {
      labels: {
        campaign: 'Campaign Code*',
        campaignQuery: 'Campaign Code',
        description: 'Campaign Description*',
        effDate: 'Effective Date*',
        effTime: 'Time*',
        expDate: 'Expiry Date*',
        expTime: 'Time*',
        enabled: 'Campaign Enabled',
        allIntermediary: 'Permit All Intermediary'
      },
      campaignForm: {
        campaign: undefined,
        description: undefined,
        effDate: undefined,
        expDate: undefined,
        enabled: false,
        allIntermediary: false,
        revision: undefined
      },
      menu: [],
      newCampaign: false,
      submitting: false,
      effDate: undefined,
      effTime: undefined,
      expDate: undefined,
      expTime: undefined
    }
  },
  computed: {
    ...mapGetters([
      'checkAccess'
    ]),
    expDateValidation() {
      return 'required|start_enddate:' + this.campaignForm.effDate
    },
    globalCampaignSideMenu() {
      return this.$store.state.promotionManagement.campaign.sideMenu
    }
  },
  methods: {
    ...mapActions([
      'campaignInit'
    ]),
    async doInit() {
      if (!this.newCampaign) {
        // Review
        await this.reviewCampaign(this.campaign)
      }
    },
    async reviewCampaign(xCampaign) {
      const apiURI = this.$store.state.promotionManagement.menu.campaignGet
      if (apiURI) {
        try {
          const expand = {
            campaign: xCampaign
          }
          const result = await this.$http.get(expandURI(apiURI, expand))
          const { data } = result.data
          if (data) {
            // Campaign init
            this.campaignForm.campaign = data.campaign.campaign
            this.campaignForm.description = data.campaign.description
            this.campaignForm.effDate = data.campaign.effDate
            this.campaignForm.expDate = data.campaign.expDate
            this.campaignForm.allIntermediary = data.campaign.allIntermediary
            this.campaignForm.enabled = data.campaign.enabled
            this.campaignForm.revision = data.campaign.revision
            if (this.campaignForm.effDate) {
              this.effDate = this.campaignForm.effDate
              this.effTime = formatDisplayTime(this.campaignForm.effDate)
            }
            if (this.campaignForm.expDate) {
              this.expDate = this.campaignForm.expDate
              this.expTime = formatDisplayTime(this.campaignForm.expDate)
            }

            // Extract menu
            this.menu = extractMenu(result.data._links)
          }
        } catch (error) {
          throw error
        }
      } else {
        this.$snotify.error('Invalid API URL')
      }
    },
    doEffDate() {
      // Format Effective Date
      if (this.effDate && this.effTime && this.effTime.length === 5) {
        const hr = this.effTime.substring(0, 2)
        const min = this.effTime.substring(3, 5)
        this.effDate = toDate(this.effDate)
        this.effDate.setHours(hr)
        this.effDate.setMinutes(min)
        this.campaignForm.effDate = this.effDate
      }
    },
    doExpDate() {
      // Format Expiry Date
      if (this.expDate && this.expTime && this.expTime.length === 5) {
        const hr = this.expTime.substring(0, 2)
        const min = this.expTime.substring(3, 5)
        this.expDate = toDate(this.expDate)
        this.expDate.setHours(hr)
        this.expDate.setMinutes(min)
        this.campaignForm.expDate = this.expDate
      }
    },
    doSave() {
      this.resetMessages()
      this.$validator.validateAll().then(result => {
        if (result) {
          if (this.newCampaign) {
            this.doCreate()
          } else {
            this.doUpdate()
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async doUpdate() {
      const apiURI = this.menu.update
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.put(apiURI, this.campaignForm)
          const { data } = result
          if (data) {
            // Show message via notify
            this.notify(data)
            await this.reviewCampaign(this.campaignForm.campaign)
            // Stay on page
            let newSideMenu = []
            newSideMenu.push({ name: 'campaignReview', text: 'Campaign', icon: 'mdi-calendar-clock' })
            newSideMenu.push({ name: 'campaignPromotionReview', text: 'Promotions', icon: 'mdi-star' })
            newSideMenu.push({ name: 'productCampaignReview', text: 'Products', icon: 'mdi-cube-outline' })
            if (!this.campaignForm.allIntermediary) {
              newSideMenu.push({ name: 'intermediaryCampaignReview', text: 'Intermediaries', icon: 'mdi-account' })
            }
            this.$store.commit(CAMPAIGN_SIDEMENU, newSideMenu)
          }
          this.submitting = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
          this.submitting = false
        }
      } else {
        this.$snotify.error('Invalid API URL')
      }
    },
    async doCreate() {
      const apiURI = this.$store.state.promotionManagement.menu.campaignCreate
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.post(apiURI, this.campaignForm)
          const { data } = result

          if (data) {
            // Show message via notify
            this.notify(data)

            // Stay on page
            this.newCampaign = false
            this.$router.push({ name: 'campaignReview', params: { campaign: this.campaignForm.campaign } })
            await this.campaignInit(this.campaignForm.campaign)
            await this.reviewCampaign(this.campaignForm.campaign)

            let newSideMenu = []
            newSideMenu.push({ name: 'campaignReview', text: 'Campaign', icon: 'mdi-calendar-clock' })
            newSideMenu.push({ name: 'campaignPromotionReview', text: 'Promotions', icon: 'mdi-star' })
            newSideMenu.push({ name: 'productCampaignReview', text: 'Products', icon: 'mdi-cube-outline' })
            if (!this.campaignForm.allIntermediary) {
              newSideMenu.push({ name: 'intermediaryCampaignReview', text: 'Intermediaries', icon: 'mdi-account' })
            }
            this.$store.commit(CAMPAIGN_SIDEMENU, newSideMenu)
          }
          this.submitting = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
          this.submitting = false
        }
      } else {
        this.$snotify.error('Invalid API URL')
      }
    },
    doQuery() {
      this.$router.push({ name: 'campaignQuery', params: { campaign: this.campaign } })
    }
  },
  created() {
    if (this.campaign === 'New') {
      this.newCampaign = true
      this.effTime = '00:00'
      this.expTime = '00:00'
    } else {
      this.doInit()
    }
  },
  props: {
    campaign: {
      type: String,
      default: ''
    }
  },
  mixins: [
    MessageMixin
  ]
}
</script>

<style lang="scss">

</style>
